import { ServerStep, ServerStepGroup } from './ServerWorkflowType';

/**
 * Finds a step group node in the tree by its workflowStepGroupId.
 * @param tree - The step group tree to search
 * @param targetId - The workflowStepGroupId to find
 * @returns The found step group or null if not found
 */
export const findNodeById = (
  tree: ServerStepGroup,
  targetId?: number | null
): ServerStepGroup | null => {
  if (!tree || targetId === null || targetId === undefined) {
    return null;
  }

  // Check if current group matches
  if (tree.workflowStepGroupId === targetId) {
    return tree;
  }

  // Recursively search in steps' nested and failback groups
  if (tree.steps) {
    for (const step of tree.steps) {
      if (step.nestedStepGroup) {
        const foundInNested = findNodeById(step.nestedStepGroup, targetId);
        if (foundInNested) {
          return foundInNested;
        }
      }
      if (step.failbackStepGroup) {
        const foundInFailback = findNodeById(step.failbackStepGroup, targetId);
        if (foundInFailback) {
          return foundInFailback;
        }
      }
    }
  }

  // Recursively search in nextStepGroup
  if (tree.nextStepGroup) {
    const foundInNext = findNodeById(tree.nextStepGroup, targetId);
    if (foundInNext) {
      return foundInNext;
    }
  }

  return null;
};

/**
 * Finds a step in the tree by its workflowStepId or workflowStepGroupId.
 * @param tree - The step group tree to search
 * @param targetId - The workflowStepId or workflowStepGroupId to find
 * @returns The found step or null if not found
 */
export const findStepById = (
  tree: ServerStepGroup,
  targetId?: number | null
): ServerStep | null => {
  if (targetId === null || targetId === undefined) {
    return null;
  }

  // Search in current level steps
  if (tree.steps) {
    for (const step of tree.steps) {
      // change value in atom
      if (step.workflowStepId === targetId) {
        return step;
      }
      // add new step group
      if (step.workflowStepGroupId === targetId) {
        return step;
      }

      // Recursively search in nested step group
      if (step.nestedStepGroup) {
        const foundInNested = findStepById(step.nestedStepGroup, targetId);
        if (foundInNested) {
          return foundInNested;
        }
      }

      // Recursively search in failback step group
      if (step.failbackStepGroup) {
        const foundInFailback = findStepById(step.failbackStepGroup, targetId);
        if (foundInFailback) {
          return foundInFailback;
        }
      }
    }
  }

  // Recursively search in next step group
  if (tree.nextStepGroup) {
    const foundInNext = findStepById(tree.nextStepGroup, targetId);
    if (foundInNext) {
      return foundInNext;
    }
  }

  return null;
};

/**
 * Updates a step in the workflow tree with new field values.
 *
 * @param tree - The workflow tree to modify
 * @param targetId - The workflowStepId of the step to update
 * @param updatedFields - Partial step object with fields to update
 * @returns A new tree with the step updated, or the original tree if step not found
 */
export const updateStepById = (
  tree: ServerStepGroup | null,
  targetId: number | null,
  updatedFields: Partial<ServerStep>
): ServerStepGroup | null => {
  if (!tree || targetId === null || targetId === undefined) {
    return tree;
  }

  const treeCopy = structuredClone(tree);

  const updateStepInGroup = (group: ServerStepGroup): boolean => {
    if (group.steps) {
      for (let index = 0; index < group.steps.length; index++) {
        const step = group.steps[index];

        if (step.workflowStepId === targetId) {
          // Update the step with new fields
          group.steps[index] = { ...step, ...updatedFields };
          return true;
        }

        // Recursively search in nested step group
        if (step.nestedStepGroup && updateStepInGroup(step.nestedStepGroup)) {
          return true;
        }

        // Recursively search in failback step group
        if (
          step.failbackStepGroup &&
          updateStepInGroup(step.failbackStepGroup)
        ) {
          return true;
        }
      }
    }

    // Recursively search in next step group
    if (group.nextStepGroup && updateStepInGroup(group.nextStepGroup)) {
      return true;
    }

    return false;
  };

  updateStepInGroup(treeCopy);
  return treeCopy;
};

/**
 * Creates and attaches a new step group as the nextStepGroup of a target step group.
 *
 * @param tree - The workflow tree to modify
 * @param updatedGroup - The new step group to attach
 * @param targetWorkflowStepGroupId - The workflowStepGroupId where to attach the new group
 * @returns A new tree with the group attached, or the updatedGroup if no target found
 */
export const createAndAttachNextStepGroup = (
  tree: ServerStepGroup | null,
  updatedGroup: ServerStepGroup,
  targetWorkflowStepGroupId?: number | null
): ServerStepGroup | null => {
  if (
    !tree ||
    targetWorkflowStepGroupId === null ||
    targetWorkflowStepGroupId === undefined
  ) {
    return updatedGroup;
  }

  const treeCopy = structuredClone(tree);
  const targetNode = findNodeById(treeCopy, targetWorkflowStepGroupId);

  if (!targetNode) {
    return treeCopy;
  }

  if (!targetNode.nextStepGroup) {
    // Set parentWorkflowStepGroupId to match the target's parent
    const updatedGroupCopy = structuredClone(updatedGroup);
    updatedGroupCopy.parentWorkflowStepGroupId =
      targetNode.parentWorkflowStepGroupId;
    targetNode.nextStepGroup = updatedGroupCopy;
  }

  return treeCopy;
};

/**
 * Creates and inserts a new step group before a target step group in the workflow tree.
 *
 * @param tree - The workflow tree to modify
 * @param newGroup - The new step group to insert
 * @param targetWorkflowStepGroupId - The workflowStepGroupId before which to insert
 * @returns A new tree with the group inserted, or the newGroup if no tree exists
 */
export const createPreviousStepGroup = (
  tree: ServerStepGroup | null,
  newGroup: ServerStepGroup,
  targetWorkflowStepGroupId?: number | null
): ServerStepGroup | null => {
  if (
    !tree ||
    targetWorkflowStepGroupId === null ||
    targetWorkflowStepGroupId === undefined
  ) {
    return newGroup;
  }

  // If the target is the root node, insert before it
  if (tree.workflowStepGroupId === targetWorkflowStepGroupId) {
    const newGroupCopy = structuredClone(newGroup);
    // New group should have the same parent as the tree
    newGroupCopy.parentWorkflowStepGroupId = tree.parentWorkflowStepGroupId;
    newGroupCopy.nextStepGroup = tree;
    return newGroupCopy;
  }

  const treeCopy = structuredClone(tree);
  const newGroupCopy = structuredClone(newGroup);
  const targetNode = findNodeById(treeCopy, targetWorkflowStepGroupId);

  // Set parentWorkflowStepGroupId to match the target's parent
  if (targetNode) {
    newGroupCopy.parentWorkflowStepGroupId =
      targetNode.parentWorkflowStepGroupId;
  }

  const insertBeforeTarget = (group: ServerStepGroup): boolean => {
    // Check if the current group's nextStepGroup is our target
    if (
      group.nextStepGroup?.workflowStepGroupId === targetWorkflowStepGroupId
    ) {
      const targetNode = group.nextStepGroup;
      newGroupCopy.nextStepGroup = targetNode;
      group.nextStepGroup = newGroupCopy;
      return true;
    }

    // Recursively search in steps' nested and failback groups
    if (group.steps) {
      for (const step of group.steps) {
        if (step.nestedStepGroup) {
          if (
            step.nestedStepGroup.workflowStepGroupId ===
            targetWorkflowStepGroupId
          ) {
            const targetNode = step.nestedStepGroup;
            // For nested groups, parent is the step's workflowStepGroupId
            newGroupCopy.parentWorkflowStepGroupId = step.workflowStepGroupId;
            newGroupCopy.nextStepGroup = targetNode;
            step.nestedStepGroup = newGroupCopy;
            return true;
          }
          if (insertBeforeTarget(step.nestedStepGroup)) {
            return true;
          }
        }

        if (step.failbackStepGroup) {
          if (
            step.failbackStepGroup.workflowStepGroupId ===
            targetWorkflowStepGroupId
          ) {
            const targetNode = step.failbackStepGroup;
            // For failback groups, parent is the step's workflowStepGroupId
            newGroupCopy.parentWorkflowStepGroupId = step.workflowStepGroupId;
            newGroupCopy.nextStepGroup = targetNode;
            step.failbackStepGroup = newGroupCopy;
            return true;
          }
          if (insertBeforeTarget(step.failbackStepGroup)) {
            return true;
          }
        }
      }
    }

    // Recursively search in nextStepGroup
    if (group.nextStepGroup) {
      if (insertBeforeTarget(group.nextStepGroup)) {
        return true;
      }
    }

    return false;
  };

  insertBeforeTarget(treeCopy);
  return treeCopy;
};

/**
 * Helper function to determine if a step matches the target ID.
 * Used by both deleteById and collectAffectedIdsByDeletion.
 */
const createIsTargetStepChecker = (
  targetId: number,
  isNodeDeletion: boolean
) => {
  return (step: ServerStep): boolean => {
    if (isNodeDeletion) {
      return step.workflowStepGroupId === targetId;
    }
    // For step deletion, only check workflowStepId
    return step.workflowStepId === targetId;
  };
};

/**
 * Deletes a node or step from the workflow tree by its ID.
 * Automatically determines whether to delete a node (by workflowStepGroupId) or step (by workflowStepId).
 * When a step group becomes empty after deletion, it is replaced by its nextStepGroup.
 *
 * @param tree - The workflow tree to modify
 * @param targetId - The workflowStepId or workflowStepGroupId to delete
 * @returns A new tree with the deletion applied, or null if the tree becomes empty
 */
export const deleteById = (
  tree: ServerStepGroup | null,
  targetId?: number | null
) => {
  if (!targetId) {
    return tree;
  }
  if (!tree) {
    return null;
  }

  const foundNode = findNodeById(tree, targetId);
  const foundStep = foundNode ? null : findStepById(tree, targetId);

  if (!foundNode && !foundStep) {
    return tree;
  }

  const isNodeDeletion = !!foundNode;
  const treeCopy = structuredClone(tree);

  const isEmptyGroup = (group: ServerStepGroup): boolean => {
    return !group.steps || group.steps.length === 0;
  };

  const replaceWithNextIfEmpty = (
    group: ServerStepGroup | null
  ): ServerStepGroup | null => {
    if (!group || isEmptyGroup(group)) {
      return group?.nextStepGroup || null;
    }
    return group;
  };

  const isTargetStep = createIsTargetStepChecker(targetId, isNodeDeletion);

  const removeFromTree = (group: ServerStepGroup): boolean => {
    if (isNodeDeletion && group.workflowStepGroupId === targetId) {
      if (group.steps && group.steps.length > 0) {
        group.steps = [];
        return true;
      }
    }

    // Only check individual steps if we're doing step deletion, not node deletion
    if (!isNodeDeletion && group.steps) {
      const stepIndex = group.steps.findIndex(isTargetStep);

      if (stepIndex !== -1) {
        group.steps.splice(stepIndex, 1);

        if (group.steps.length === 0) {
          group.steps = undefined;
        }
        return true;
      }
    }

    // Search in nested groups (for both node and step deletion)
    if (group.steps) {
      for (const step of group.steps) {
        if (step.nestedStepGroup && removeFromTree(step.nestedStepGroup)) {
          step.nestedStepGroup = replaceWithNextIfEmpty(step.nestedStepGroup);
          return true;
        }

        if (step.failbackStepGroup && removeFromTree(step.failbackStepGroup)) {
          step.failbackStepGroup = replaceWithNextIfEmpty(
            step.failbackStepGroup
          );
          return true;
        }
      }
    }

    if (group.nextStepGroup && removeFromTree(group.nextStepGroup)) {
      group.nextStepGroup = replaceWithNextIfEmpty(group.nextStepGroup);
      return true;
    }

    return false;
  };

  removeFromTree(treeCopy);

  if (isEmptyGroup(treeCopy)) {
    return treeCopy.nextStepGroup || null;
  }

  return treeCopy;
};

type AffectedIdsResult = {
  workflowRemovableStepGroupIds: number[];
  workflowRemovableStepIds: number[];
};

/**
 * Collects all step and step group IDs that would be affected by deleting a target ID.
 * This includes the target itself, all nested content, and any groups that would become empty.
 * Useful for preview/confirmation before actual deletion.
 *
 * @param tree - The workflow tree to analyze
 * @param targetId - The workflowStepId or workflowStepGroupId to check for deletion
 * @returns An object containing arrays of affected step group IDs and step IDs
 */
export const collectAffectedIdsByDeletion = (
  tree: ServerStepGroup | null,
  targetId: number | null
): AffectedIdsResult => {
  const result: AffectedIdsResult = {
    workflowRemovableStepGroupIds: [],
    workflowRemovableStepIds: [],
  };

  if (!tree || targetId === null || targetId === undefined) {
    return result;
  }

  const foundNode = findNodeById(tree, targetId);
  const foundStep = foundNode ? null : findStepById(tree, targetId);

  if (!foundNode && !foundStep) {
    return result;
  }

  const isNodeDeletion = !!foundNode;

  const addStepGroupId = (id: number | null | undefined) => {
    if (id) {
      result.workflowRemovableStepGroupIds.push(id);
    }
  };

  const addStepId = (id: number | null | undefined) => {
    if (id) {
      result.workflowRemovableStepIds.push(id);
    }
  };

  const collectIdsFromGroup = (
    group: ServerStepGroup | null | undefined
  ): void => {
    if (!group) {
      return;
    }

    addStepGroupId(group.workflowStepGroupId);

    if (group.steps) {
      for (const step of group.steps) {
        addStepId(step.workflowStepId);
        addStepGroupId(step.workflowStepGroupId);
        collectIdsFromGroup(step.nestedStepGroup);
        collectIdsFromGroup(step.failbackStepGroup);
      }
    }

    collectIdsFromGroup(group.nextStepGroup);
  };

  const collectIdsFromStep = (step: ServerStep): void => {
    addStepId(step.workflowStepId);
    addStepGroupId(step.workflowStepGroupId);
    collectIdsFromGroup(step.nestedStepGroup);
    collectIdsFromGroup(step.failbackStepGroup);
  };

  const isTargetStep = createIsTargetStepChecker(targetId, isNodeDeletion);

  const shouldReplaceEmptyGroup = (
    group: ServerStepGroup | null | undefined
  ): boolean => {
    return !group || !group.steps || group.steps.length === 0;
  };

  const processNestedGroup = (group: ServerStepGroup): boolean => {
    const found = findAndCollectAffectedIds(group);
    if (found && shouldReplaceEmptyGroup(group)) {
      addStepGroupId(group.workflowStepGroupId);
    }
    return found;
  };

  const findAndCollectAffectedIds = (group: ServerStepGroup): boolean => {
    if (isNodeDeletion && group.workflowStepGroupId === targetId) {
      addStepGroupId(group.workflowStepGroupId);
      if (group.steps) {
        for (const step of group.steps) {
          collectIdsFromStep(step);
        }
      }
      return true;
    }

    // Only check individual steps if we're doing step deletion, not node deletion
    if (!isNodeDeletion && group.steps) {
      const stepIndex = group.steps.findIndex(isTargetStep);

      if (stepIndex !== -1) {
        collectIdsFromStep(group.steps[stepIndex]);

        if (group.steps.length === 1) {
          addStepGroupId(group.workflowStepGroupId);
        }
        return true;
      }
    }

    if (group.steps) {
      for (const step of group.steps) {
        if (step.nestedStepGroup && processNestedGroup(step.nestedStepGroup)) {
          return true;
        }
        if (
          step.failbackStepGroup &&
          processNestedGroup(step.failbackStepGroup)
        ) {
          return true;
        }
      }
    }

    if (group.nextStepGroup && processNestedGroup(group.nextStepGroup)) {
      return true;
    }

    return false;
  };

  findAndCollectAffectedIds(tree);

  if (shouldReplaceEmptyGroup(tree)) {
    addStepGroupId(tree.workflowStepGroupId);
  }

  return result;
};
